/**
 * Given a category, subcategory, identifier and query string, this function will
 * construct and return the resource endpoint for a category page.
 *
 * The url host is pulled from config provided by @lululemon/ecom-core.
 *
 * @module services/atg
 *
 * @author Michael McManus <michaelmcmanus@lululemon.com>
 * @author Jesse Weed <jeweed@deloitte.com>
 */

// UTILITIES
import queryString from "query-string";
import url from "url";
import { sanitize, stripNtt } from "../lib/util";
import { getEcId, getSessionId } from "../lib/signal-creator";
import { publicConfig } from "../util/public-config";

export const atg = {};

/**
 * Utility to determine the base url for API calls
 * @param state
 * @returns {string}
 */
export function getBaseUrl() {
  return url.resolve(publicConfig.apiHost, publicConfig.apiHostBasePath);
}

export function getBaseHost() {
  return publicConfig.apiHost;
}

/**
 * Get URL for CDP API
 * @param category
 * @param subcategory
 * @param identifier
 * @param queryStringArg
 * @returns {string}
 */
export function getCategoryPageUrl(path, queryStringArg) {
  const apiHost = getBaseUrl();
  let cdpQueryString = queryStringArg;

  if (!!queryStringArg && !queryStringArg.startsWith("?")) {
    cdpQueryString = `?${queryStringArg}`;
  }

  return `${apiHost}${path}${cdpQueryString}`;
}

/**
 * Get URL for Product Detail Page
 * @param category
 */
export function getProductDetailUrl(path) {
  return `${getBaseUrl()}${path}`;
}

export function getBagUrl() {
  return `${getBaseUrl()}/bag`;
}

export function getBraQuizRecommendedSizesUrl() {
  return `${getBaseUrl()}/content/recommendBraSize`;
}

/**
 * Get URL for store inventory for specified SKUs and stores
 * For use in BOPIS Store Availability Modal
 * @returns {string}
 */
export function getBopisStoreInventoryUrl(params) {
  return `${getBaseUrl()}/inventory?${params}`;
}

/**
 * Get URL for all store location data
 * For use in Store Availability Modal
 * @returns {string}
 */
export function getStoresLocationUrl() {
  return `${getBaseUrl()}/stores`;
}

/**
 * Auto suggest API. Defunct now
 * @returns {string}
 */

export function getAutoSuggestUrl() {
  return `${getBaseUrl()}/autoSuggest?&Dy=1`;
}

/** Auto Complete API
 * @returns {string}
 */

export function getAutoCompleteUrl(searchTerm) {
  const queryObj = {
    Ntt: searchTerm,
  };
  const qs = queryString.stringify(queryObj);
  return `${getBaseUrl()}/autoComplete?${qs}`;
}

/** Search API data
 * @returns {string}
 */

export function getSearchUrl(searchTermArg, pagingParams, useLWApi) {
  const searchTerm = searchTermArg.replace('"', "").trim();
  const Ntt = stripNtt(searchTerm.replace(/\s/g, "+"));
  const searchPath = useLWApi ? "s" : "search";
  let query = {
    Ntt,
    page: pagingParams.page,
    page_size: pagingParams.pageSize,
  };
  if (useLWApi) {
    query = {
      Ntt,
      "page[offset]":
        pagingParams.page > 1
          ? (pagingParams.page - 1) * pagingParams.pageSize
          : 0,
      "page[limit]": pagingParams.pageSize,
    };
  }
  return `${getBaseUrl()}/${searchPath}?${queryString.stringify(query)}`;
}

/** Navigation Search URL
 * @returns {string}
 */

export function getNavigationSearchUrl(
  searchNavigationUrl,
  pagingParams,
  useLWAPI
) {
  const arr = searchNavigationUrl.split("?");
  const parsedQs = queryString.parse(arr[1]);
  const sessionID = getSessionId();
  const userID = getEcId();

  delete parsedQs.format;
  delete parsedQs.Nrpp;

  // LW check
  if (useLWAPI) {
    parsedQs["page[offset]"] =
      pagingParams.page > 1
        ? (pagingParams.page - 1) * pagingParams.pageSize
        : 0;
    if (pagingParams.pageSize) {
      parsedQs["page[limit]"] = pagingParams.pageSize;
    }
  } else {
    parsedQs.page = pagingParams.page;
    if (pagingParams.pageSize) {
      parsedQs.page_size = pagingParams.pageSize;
    }
  }
  const term = (sanitize(parsedQs.Ntt) || "").replace(/\s/g, "+");
  parsedQs.Ntt = term;

  parsedQs.session = sessionID;
  parsedQs.userId = userID;

  // UrlEncode all parma vals
  const qs = Object.keys(parsedQs)
    .map((key) => {
      if (!Array.isArray(parsedQs[key])) {
        return `${key}=${encodeURIComponent(parsedQs[key])}`;
      }
      return parsedQs[key]
        .map((val) => `${key}=${encodeURIComponent(val)}`)
        .join("&");
    })
    .join("&");

  const relativeUrl = searchNavigationUrl.split("?")[0];
  if (useLWAPI) {
    return `${getBaseUrl()}/s?${qs}`;
  }
  return `${getBaseUrl()}/search/${relativeUrl}?${qs}`;
}

/** Submit Review URL
 * @returns {string}
 */
export function getSubmitReviewUrl() {
  return publicConfig.links.submitReview;
}

/** Host Name
 * @returns {string}
 */
export function getHostName() {
  return url.parse(publicConfig.apiHost).host;
}

export function getCurrentEnvironment(apiHost) {
  return apiHost && apiHost.split("//")[1].split(".lululemon")[0];
}

/** Returns a boolean indicating if the URL is Production
 * @returns {boolean}
 */
export function isProductionEnvironment() {
  if (typeof window !== "undefined") {
    return window.location.href.includes("shop");
  }

  return false;
}

/** Returns a boolean indicating if the URL is Preview
 * @returns {boolean}
 */
export function isPreviewEnvironment() {
  if (typeof window !== "undefined") {
    return window.location.href.includes("preview");
  }

  return false;
}
